import { useState, useMemo } from "react";
import { Autocomplete, AutocompleteItem, Spinner } from "@heroui/react";
import { LuPlus } from "react-icons/lu";
import { usePostData } from "@/hooks";
import { showToast } from "@/utils";

/**
 * CreatableAutocomplete - An Autocomplete component that allows creating new options
 *
 * @param {Object} props
 * @param {Array} props.items - Array of { value, title } objects
 * @param {string} props.selectedKey - Currently selected key
 * @param {Function} props.onSelectionChange - Called when selection changes
 * @param {string} props.storeUrl - API endpoint for creating new items (e.g., "payment-method/store")
 * @param {string} props.storePayloadKey - Key name for the payload (default: "name")
 * @param {Function} props.onCreateSuccess - Optional callback after successful creation, receives new item { value, title }
 * @param {string} props.createLabel - Label pattern for create option (default: "Buat")
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Input placeholder
 * @param {Object} props.itemProps - Props to spread onto each AutocompleteItem
 * @param {any} props.props - Other Autocomplete props
 */
const CreatableAutocomplete = ({
    items = [],
    selectedKey,
    onSelectionChange,
    storeUrl,
    storePayloadKey = "name",
    onCreateSuccess,
    createLabel = "Buat",
    label,
    placeholder,
    isLoading,
    itemProps = {},
    ...props
}) => {
    const [localItems, setLocalItems] = useState([]);
    const [createValue, setCreateValue] = useState("");

    // API hook for creating new items
    const storeItem = usePostData(storeUrl);

    // Combine passed items with locally created items
    const allItems = useMemo(() => {
        return [...items, ...localItems];
    }, [items, localItems]);

    // Build display items - all items plus create option if needed
    const displayItems = useMemo(() => {
        const result = [...allItems];

        // Add create option if there's a value to create and no exact match exists
        if (createValue.trim() && storeUrl) {
            const exactMatch = allItems.some(
                (item) =>
                    item.title.toLowerCase() === createValue.toLowerCase(),
            );
            if (!exactMatch) {
                result.push({
                    value: "__create__",
                    title: `${createLabel} "${createValue.trim()}"`,
                    isCreateOption: true,
                });
            }
        }

        return result;
    }, [allItems, createValue, storeUrl, createLabel]);

    const handleCreate = async () => {
        if (!storeUrl || !createValue.trim()) return;

        try {
            const result = await storeItem.mutate({
                [storePayloadKey]: createValue.trim(),
            });

            if (result?.data?.id) {
                const newItem = {
                    value: String(result.data.id),
                    title: result.data.name || result.data[storePayloadKey],
                };

                // Add to local items
                setLocalItems((prev) => [...prev, newItem]);

                // Select the new item
                onSelectionChange?.(newItem.value);

                // Clear create value
                setCreateValue("");

                // Show success toast
                showToast(
                    "success",
                    "Berhasil",
                    `${newItem.title} berhasil dibuat`,
                );

                // Call optional callback
                onCreateSuccess?.(newItem);
            } else if (result?.message) {
                // Handle validation errors from API
                showToast("error", "Gagal", result.message);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Gagal membuat data baru";
            showToast("error", "Gagal", errorMessage);
        }
    };

    const handleSelectionChange = (key) => {
        if (key === "__create__") {
            handleCreate();
        } else {
            onSelectionChange?.(key);
            setCreateValue("");
        }
    };

    const handleInputChange = (value) => {
        // Track input for create option
        setCreateValue(value);
    };

    return (
        <Autocomplete
            label={label}
            placeholder={placeholder}
            defaultItems={displayItems}
            selectedKey={selectedKey ? String(selectedKey) : null}
            onSelectionChange={handleSelectionChange}
            onInputChange={handleInputChange}
            isLoading={isLoading || storeItem.isLoading}
            allowsCustomValue
            listboxProps={{
                emptyContent: storeUrl
                    ? "Ketik untuk mencari atau membuat baru"
                    : "Tidak ada hasil",
            }}
            {...props}
        >
            {(item) => (
                <AutocompleteItem
                    key={item.value}
                    textValue={item.title}
                    startContent={
                        item.isCreateOption ? (
                            storeItem.isLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                <LuPlus size={14} />
                            )
                        ) : null
                    }
                    {...itemProps}
                >
                    {item.title}
                </AutocompleteItem>
            )}
        </Autocomplete>
    );
};

export default CreatableAutocomplete;
